/**
 * Morse — die Hilfen für den Einstieg: der Schalter „Hilfe“, das Kennenlernen neuer Zeichen und der Spickzettel
 * mit den Zeichen der Stufe. (06.10.2026, Martin: „So ist es mir noch viel zu schwer.“)
 *
 *   - Hilfe beim Hören: „Aus“ · „Nach dem Ton“ (das Muster erscheint, sobald die Gruppe verklungen ist — nicht
 *     vorher, sonst lernt man das Sehen statt das Hören) · „Vorher zeigen“ · „Immer“ (08.10.2026, Martin: die Karten
 *     „Deine Zeichen“ sollen beim Üben DAUERHAFT unten stehen — vom Start der Runde an und auch, während es klingt;
 *     das Muster der Gruppe kommt wie bei „Nach dem Ton“ erst nach der Antwort). Beim Geben: „Aus“ · „An“ (Silben zur
 *     Vorlage, Spickzettel unter der Taste). Vorgabe beim Geben AN bis Stufe `HILFE_VORGABE_BIS_STUFE`, beim Hören
 *     „Immer“ bis Stufe `HILFE_IMMER_BIS_STUFE`, danach aus; eine eigene Wahl wird gemerkt und gilt dann über die
 *     Stufen hinweg — eine gemerkte Wahl überschreibt die Vorgabe nie.
 *   - Kennenlernen: ein Zeichen gilt als kennengelernt, sobald auf seiner Stufe geübt wurde (gemerkt) — oder wenn
 *     der Stand vom Server zeigt, dass es schon gehört ist (dann auch auf einem anderen Gerät).
 *
 * Gemerkt wird am GERÄT (localStorage): beides ist eine Bequemlichkeit der Anzeige, keine Statistik. Die Koch-Regel
 * und die Buchungen sind davon unberührt; ob eine Runde mit Hilfe lief, sagt die Diagnosezeile („hilfe=an“).
 * Privatmodus oder gesperrter Speicher: dann gilt die Vorgabe, nichts wirft.
 */
import { kochNeueZeichen, kochStufe, kochUebungsZeichen, umfangVon, type Umfang } from "../../shared/morse-koch.js";
import { MORSE_TABELLE } from "../../shared/morse.js";
import { musterBild, musterSprechen } from "./morse-bild.js";
import { merkwortAria, merkwortElement } from "./morse-merkwort.js";
import { HILFE, UMFANG } from "./morse-texte.js";
import { el, type ZeichenStand } from "./morse-teile.js";

export type HoerHilfe = "aus" | "nach" | "vorab" | "immer";
export type GebHilfe = "aus" | "an";
interface HilfeWahl {
  hoeren: HoerHilfe;
  geben: GebHilfe;
}

/** Bis zu dieser Stufe ist die Hilfe beim Geben von selbst an. */
const HILFE_VORGABE_BIS_STUFE = 4;
/** Bis zu dieser Stufe stehen beim Hören die Karten „Deine Zeichen“ von selbst die ganze Runde da („Immer“). */
const HILFE_IMMER_BIS_STUFE = 5;
const HILFE_SCHLUESSEL = "vespera.morse.hilfe";
const KENNEN_SCHLUESSEL = "vespera.morse.kennengelernt";

/** Die Vorgabe einer Stufe — gilt nur, wo nichts gemerkt ist (`liesHilfe`). */
export function hilfeVorgabe(stufe: number): HilfeWahl {
  const s = kochStufe(stufe);
  return { hoeren: s <= HILFE_IMMER_BIS_STUFE ? "immer" : "aus", geben: s <= HILFE_VORGABE_BIS_STUFE ? "an" : "aus" };
}

/** Roh aus dem Gerätespeicher — `null` bei Fehlen, Sperre oder kaputtem JSON. Der Typ ist NICHT geprüft. */
function lies(schluessel: string): unknown {
  try {
    const roh = localStorage.getItem(schluessel);
    return roh ? (JSON.parse(roh) as unknown) : null;
  } catch {
    return null;
  }
}

/**
 * Im Gerätespeicher kann alles stehen (alte Fassung, Hand, anderes Programm): eine Liste nur, wenn es eine ist,
 * und darin nur Zeichenketten (Prüfbefund K5: ein Objekt ließ `new Set(…)` werfen, ein Text wurde zu Buchstaben).
 */
function liesListe(schluessel: string): string[] {
  const x = lies(schluessel);
  return Array.isArray(x) ? x.filter((z): z is string => typeof z === "string") : [];
}

/** Ein schlichtes Objekt — sonst ein leeres (eine Zahl, ein Text oder eine Liste gilt als „nichts gemerkt“). */
function liesObjekt(schluessel: string): Record<string, unknown> {
  const x = lies(schluessel);
  return x && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : {};
}

function schreib(schluessel: string, wert: unknown): void {
  try {
    localStorage.setItem(schluessel, JSON.stringify(wert));
  } catch {
    /* Privatmodus: dann eben nur für jetzt */
  }
}

/** Was gilt: die gemerkte Wahl — wo keine gemerkt ist, die Vorgabe der Stufe. */
export function liesHilfe(stufe: number): HilfeWahl {
  const v = hilfeVorgabe(stufe);
  const g = liesObjekt(HILFE_SCHLUESSEL);
  const h = g.hoeren;
  const b = g.geben;
  return {
    hoeren: h === "aus" || h === "nach" || h === "vorab" || h === "immer" ? h : v.hoeren,
    geben: b === "aus" || b === "an" ? b : v.geben,
  };
}

/**
 * Die Kurztabelle beim Geben (08.10.2026, Martin): ein Schalter „Tabelle“ nahe der Taste blendet die Zeichen der Stufe
 * mit ihrem Muster ein. Standard AUS; gemerkt wie die Hilfe-Wahl (gleicher Gerätespeicher, eigener Schlüssel darin) —
 * Lesen schreibt nie, eine gemerkte Wahl wird nur durch einen Tipp auf den Schalter geändert.
 */
export function liesKurztabelle(): boolean {
  return liesObjekt(HILFE_SCHLUESSEL).kurztabelle === "an";
}

export function merkeKurztabelle(an: boolean): void {
  schreib(HILFE_SCHLUESSEL, { ...liesObjekt(HILFE_SCHLUESSEL), kurztabelle: an ? "an" : "aus" });
}

/**
 * „Üben mit“ (08.10.2026): Meine Zeichen oder Alle Zeichen — EINE Wahl für Hören und Geben, gemerkt am Gerät im selben
 * Speicher wie die Hilfe-Wahl (eigener Schlüssel darin). Vorgabe „Meine Zeichen“: wo nichts gemerkt ist oder Unsinn
 * steht, ändert sich nichts. Lesen schreibt nie.
 */
export function liesUmfang(): Umfang {
  return umfangVon(liesObjekt(HILFE_SCHLUESSEL).umfang);
}

export function merkeUmfang(umfang: Umfang): void {
  schreib(HILFE_SCHLUESSEL, { ...liesObjekt(HILFE_SCHLUESSEL), umfang });
}

/**
 * „Augen zu“: ob und wann der Buchstabe auf dem Bildschirm steht (13.10.2026, Martin: „zwischendurch die Augen aufmachen und
 * sehen, was es war — oder den Buchstaben ausblenden“). Vorgabe „Nach der Antwort“; am Gerät gemerkt wie der Modus.
 */
export type BuchstabeZeigen = "aus" | "nach" | "immer";

export function liesBuchstabe(): BuchstabeZeigen {
  const b = liesObjekt(HILFE_SCHLUESSEL).buchstabe;
  return b === "aus" || b === "immer" ? b : "nach";
}

export function merkeBuchstabe(b: BuchstabeZeigen): void {
  schreib(HILFE_SCHLUESSEL, { ...liesObjekt(HILFE_SCHLUESSEL), buchstabe: b });
}

/**
 * Der Modus im Reiter Hören (13.10.2026): „Tippen“ (Vorgabe) oder „Augen zu“ — gemerkt am Gerät im selben Speicher wie die
 * Hilfe-Wahl. Wer mit geschlossenen Augen übt, will beim nächsten Mal nicht erst umschalten. Lesen schreibt nie.
 */
export type HoerModus = "tippen" | "augen";

export function liesModus(): HoerModus {
  return liesObjekt(HILFE_SCHLUESSEL).modus === "augen" ? "augen" : "tippen";
}

export function merkeModus(modus: HoerModus): void {
  schreib(HILFE_SCHLUESSEL, { ...liesObjekt(HILFE_SCHLUESSEL), modus });
}

/** Eine Wahl merken (nur der eine Reiter; der andere behält seine). */
export function merkeHilfe<K extends keyof HilfeWahl>(teil: K, wert: HilfeWahl[K]): void {
  schreib(HILFE_SCHLUESSEL, { ...liesObjekt(HILFE_SCHLUESSEL), [teil]: wert });
}

/** Die neuen Zeichen dieser Stufe, die noch nicht kennengelernt sind (leer: nichts zu tun). */
export function nochKennenlernen(stufe: number, zeichen: ReadonlyArray<Pick<ZeichenStand, "zeichen" | "gehoert">>): string[] {
  const gemerkt = new Set(liesListe(KENNEN_SCHLUESSEL));
  const gehoert = new Set(zeichen.filter((z) => z.gehoert > 0).map((z) => z.zeichen));
  return kochNeueZeichen(stufe).filter((z) => !gemerkt.has(z) && !gehoert.has(z));
}

/** Diese Zeichen gelten ab jetzt als kennengelernt. */
export function merkeKennengelernt(zeichen: readonly string[]): void {
  if (!zeichen.length) return;
  const gemerkt = new Set(liesListe(KENNEN_SCHLUESSEL));
  for (const z of zeichen) gemerkt.add(z);
  schreib(KENNEN_SCHLUESSEL, [...gemerkt]);
}

/**
 * Der Spickzettel: die Zeichen der Stufe, je mit seinem Muster als Bild. Zum Ansehen, nicht zum Antippen — er steht
 * in der laufenden Übung, und ein Ton aus ihm fiele der Übung ins Wort.
 *
 * Bei „Alle Zeichen“ (08.10.2026) stehen alle Zeichen des Kurses da: kompakt, mehrzeilig, in begrenzter Höhe mit Innen-
 * Scroll (die Liste ist dann mit der Tastatur erreichbar); was noch nie gehört wurde, steht leise abgesetzt da —
 * `data-leise` UND in Worten für Bildschirmleser, dazu eine Zeile, was „blass“ heißt.
 */
let spickNr = 0;
/** `kurz`: die kleine Form unter der Taste beim Geben — ohne sichtbaren Titel (der Schalter „Tabelle“ davor ist die Überschrift). */
export function baueSpick(stufe: number, o: { kurz?: boolean; umfang?: Umfang; zeichen?: SpickStand } = {}): HTMLElement {
  const block = el("div", o.kurz ? "morse-spick morse-spick--kurz" : "morse-spick");
  const titel = el("p", o.kurz ? "morse-spick-titel morse-nur-vorlesen" : "morse-spick-titel morse-feld-titel", HILFE.spickTitel);
  titel.id = `morse-spick-${++spickNr}`;
  const liste = el("ul", "morse-spick-liste");
  liste.setAttribute("aria-labelledby", titel.id);
  const legende = el("p", "morse-spick-legende", UMFANG.blass);
  legende.hidden = true;
  block.append(titel, liste, legende);
  zeichneSpick(block, stufe, { umfang: o.umfang, zeichen: o.zeichen });
  return block;
}

/** Was der Spickzettel vom Stand der Seite braucht: welche Zeichen schon gehört sind. */
type SpickStand = ReadonlyArray<Pick<ZeichenStand, "zeichen" | "gehoert">>;

/** Den Spickzettel auf eine (neue) Stufe bringen — oder auf „Alle Zeichen“ (`zeichen`: der Stand, woran „noch nie gehört“ hängt). */
export function zeichneSpick(spick: HTMLElement, stufe: number, o: { umfang?: Umfang; zeichen?: SpickStand } = {}): void {
  const liste = spick.matches(".morse-spick-liste") ? spick : spick.querySelector<HTMLElement>(".morse-spick-liste");
  if (!liste) return;
  const alle = o.umfang === "alle";
  const gehoert = new Set((o.zeichen ?? []).filter((z) => z.gehoert > 0).map((z) => z.zeichen));
  liste.textContent = "";
  liste.dataset.umfang = alle ? "alle" : "meine";
  // Eine Liste, die scrollt, muss mit der Tastatur erreichbar sein (Pfeile, Bild auf/ab).
  if (alle) liste.tabIndex = 0;
  else liste.removeAttribute("tabindex");
  let leise = 0;
  const mitMerkwort = !alle && spick.closest(".morse-spick--kurz") === null;
  for (const z of kochUebungsZeichen(stufe, o.umfang)) {
    const m = MORSE_TABELLE[z] ?? "";
    const li = el("li", "morse-spick-zeichen");
    const blass = alle && !gehoert.has(z);
    if (blass) {
      li.dataset.leise = "true";
      leise++;
    }
    const name = el("span", "morse-spick-name", z);
    name.setAttribute("aria-hidden", "true");
    const bild = musterBild(m, "s");
    bild.setAttribute("aria-hidden", "true");
    // Das Merkwort (15.10.2026) nur in der ruhigen Form („Meine Zeichen“, nicht unter der Taste): dort ist Platz.
    const merk = mitMerkwort ? merkwortElement(z, "morse-spick-merk") : null;
    merk?.setAttribute("aria-hidden", "true");
    const merkSatz = merk ? `, ${merkwortAria(z)}` : "";
    li.append(el("span", "morse-nur-vorlesen", `${z}: ${musterSprechen(m)}${merkSatz}${blass ? `, ${UMFANG.nochNieGehoert}` : ""}`), name, bild, ...(merk ? [merk] : []));
    liste.appendChild(li);
  }
  const block = spick.matches(".morse-spick-liste") ? spick.parentElement : spick;
  const titel = block?.querySelector<HTMLElement>(".morse-spick-titel");
  if (titel) titel.textContent = alle ? UMFANG.spickTitel : HILFE.spickTitel;
  const legende = block?.querySelector<HTMLElement>(".morse-spick-legende");
  if (legende) legende.hidden = leise === 0;
}
